import React, { FC, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import Title from "../../../../components/Title/Title";
import { InputCoefficientStyle } from "../../../../styles/styles";
import ArticleBorder from "../../../../components/ArticleBorder/ArticleBorder";

interface FormData {
    degree: number;
    coefficients: string[];
    root: number;
}

const parseCoefficients = (coeffs: string[]): number[] =>
    coeffs.map((val) => (val === "" || isNaN(Number(val)) ? 0 : parseFloat(val)));

const HornerSchemeCalculatorContent: FC = () => {
    const { register, watch, setValue } = useForm<FormData>({
        defaultValues: {
            degree: 2,
            coefficients: [],
            root: 0,
        },
    });

    const degree = watch("degree");
    const watchAll = watch();

    const [resultRow, setResultRow] = useState<number[]>([]);
    const [remainder, setRemainder] = useState<number | null>(null);

    useEffect(() => {
        setValue("coefficients", Array(degree + 1).fill(""), { shouldValidate: true });
    }, [degree, setValue]);

    useEffect(() => {
        const rawCoefficients = watchAll.coefficients;
        const x = watchAll.root;

        if (rawCoefficients && Array.isArray(rawCoefficients)) {
            const coeffs = parseCoefficients(rawCoefficients);
            const results: number[] = [];

            if (coeffs.length === degree + 1 && !isNaN(x)) {
                let temp = coeffs[0];
                results.push(temp);

                for (let i = 1; i < coeffs.length; i++) {
                    temp = temp * x + coeffs[i];
                    results.push(temp);
                }

                setResultRow(results);
                setRemainder(results[results.length - 1]);
            }
        }
    }, [watchAll, degree]);

    return (
        <>
            <Title text="Schemat Hornera" type="main-article" />

            <form className="flex flex-col gap-4 max-w-md mx-auto">
                <label className="flex flex-col items-center gap-2">
                    <span>Wybierz stopień wielomianu (2–10):</span>
                    <select {...register("degree", { valueAsNumber: true })} className={InputCoefficientStyle}>
                        {[...Array(9)].map((_, index) => (
                            <option key={index + 2} value={index + 2}>
                                {index + 2}
                            </option>
                        ))}
                    </select>
                </label>

                <div className="flex flex-col items-center gap-2">
                    <span>Wprowadź współczynniki:</span>
                    <div className="flex flex-wrap justify-center gap-2">
                        {[...Array(degree + 1)].map((_, i) => {
                            const power = degree - i;
                            return (
                                <label key={i} className="flex items-center gap-1">
                                    <input
                                        type="number"
                                        step="any"
                                        {...register(`coefficients.${i}`)}
                                        className={`${InputCoefficientStyle} w-20 text-center`}
                                    />
                                    <span>
                                        x<sup>{power}</sup>
                                    </span>
                                </label>
                            );
                        })}
                    </div>
                </div>

                <label className="flex flex-col items-center gap-2">
                    <span>Wprowadź pierwiastek do schematu Hornera:</span>
                    <input
                        type="number"
                        step="any"
                        {...register("root", { valueAsNumber: true })}
                        className={`${InputCoefficientStyle} w-32 text-center`}
                    />
                </label>
            </form>

            {resultRow.length > 0 && (
                <>
                    <ArticleBorder />
                    <div className="text-center mt-4">
                        <p className="font-semibold">Wyniki schematu Hornera:</p>
                        <div className="flex justify-center gap-2 flex-wrap mt-2">
                            {resultRow.map((val, index) => (
                                <div key={index} className="border px-4 py-2 bg-gray-100 rounded">
                                    {val.toFixed(3)}
                                </div>
                            ))}
                        </div>
                        <p className="mt-4">
                            <strong>Reszta dzielenia:</strong>{" "}
                            {remainder !== null ? remainder.toFixed(3) : "–"}
                        </p>
                    </div>
                </>
            )}
        </>
    );
};

export default HornerSchemeCalculatorContent;
